import { GetServerSideProps, NextPage } from 'next'
import { useEffect } from 'react'
import { PageCrumbs } from '~/components/business/PageCrumbs'
import { SEOHead } from '~/components/business/SEOHead'
import { GlobalRequest } from '~/components/global-request'
import { MyContainer } from '~/components/ui/MyContainer'
import { Layout } from '~/layouts/default'
import { IAICompetitionContentObj } from '~/@types/ai'
import {
  SingleCompetition,
  IAICompetitionDetailNode,
} from '~/components/pages/ai-competition/SingleCompetition'
import { Col, Row } from 'antd'
import { GET_COMPETITION_LIST } from '~/graphqls/gqls'
import {
  getCompetitionList,
  getCompetitionListVariables,
  getCompetitionList_marketingModule_competitionPage_edges_node,
} from '~/graphqls/types'
import {
  getGraphData,
  CacheValueProps,
  getScrollCache,
  setScrollCache,
} from '~/utils'
import { PAGE_SIZE } from '~/configs'
import useInfiniteScroll from '~/hooks/useInfiniteScroll'
import { useDidUpdateEffect } from '~/hooks/useDidUpdateEffect'
import useScrollTop from '~/hooks/useScrollTop'
import { MyBtn } from '~/components/ui/MyBtn'
import { MyIcon } from '~/components/ui/MyIcon'
import { useRouter } from 'next/router'

/** 字段格式化 */
function formatListRes(
  res?: getCompetitionList_marketingModule_competitionPage_edges_node | null
) {
  const contentObj: IAICompetitionContentObj =
    res?.content && JSON.parse(res?.content)
  return {
    node: {
      ...res,
      ...contentObj,
    },
  }
}

const AICompetitions: NextPage<{
  scrollCache: CacheValueProps
  list: IAICompetitionDetailNode[]
  hasNextPage: boolean
}> = ({ scrollCache, list: initList, hasNextPage: nextPage }) => {
  const scrollTop = useScrollTop()

  const { InfiniteScollComp, list } = useInfiniteScroll<
    IAICompetitionDetailNode,
    getCompetitionList,
    getCompetitionListVariables
  >({
    queryDoc: GET_COMPETITION_LIST,
    initData: initList,
    hasNextPage: nextPage,
    hasNextPageExtractor: res => {
      return (
        res.data.marketingModule?.competitionPage?.pageInfo?.hasNextPage ||
        false
      )
    },
    dataExtractor: res => {
      return getGraphData(
        res.data.marketingModule?.competitionPage?.edges?.map(it => ({
          ...formatListRes(it?.node),
        })) || []
      )
    },
  })

  const router = useRouter()
  useEffect(() => {
    window.scrollTo({ top: scrollCache.scrollTop || 0, behavior: 'auto' })
  }, [])
  useDidUpdateEffect(() => {
    setScrollCache({
      key: router.asPath,
      value: {
        path: router.asPath,
        scrollTop,
        pageSize: list.length,
      },
    })
  }, [list.length, scrollTop, router.asPath])
  return (
    <Layout>
      <SEOHead title="AI创作大赛" />
      <MyContainer>
        <PageCrumbs
          dark
          title="AI创作大赛"
          extra={
            <MyBtn
              type="default"
              className="!text-neutral-400 !border-neutral-400 hover:!border-primary !px-[10px]"
              onClick={() => router.push('/ai-competition/cooperation')}
            >
              <div className="flex items-center text-sm">
                <MyIcon icon="cooperation" className="mr-1" />
                大赛合作
              </div>
            </MyBtn>
          }
        />
        <Row gutter={32}>
          {list.map(item => (
            <Col span={12} key={item.id}>
              <SingleCompetition data={item} />
            </Col>
          ))}
        </Row>
        {InfiniteScollComp}
      </MyContainer>
    </Layout>
  )
}

export const getServerSideProps: GetServerSideProps = async context => {
  const { initialState, client } = await GlobalRequest(context)
  const scrollCache = getScrollCache(context)
  const listRes = await client.query<
    getCompetitionList,
    getCompetitionListVariables
  >({
    query: GET_COMPETITION_LIST,
    variables: {
      offset: 0,
      first: scrollCache.pageSize | PAGE_SIZE,
    },
    fetchPolicy: 'no-cache',
  })

  return {
    props: {
      initialState,
      scrollCache,
      list: getGraphData(
        listRes.data.marketingModule?.competitionPage?.edges?.map(it => ({
          ...formatListRes(it?.node),
        })) || []
      ),
      hasNextPage:
        listRes.data.marketingModule?.competitionPage?.pageInfo?.hasNextPage ||
        false,
    },
  }
}

export default AICompetitions
